// WO-56 — Admin → Settings → "AI chat assistants".
// Who sees each assistant (off / admins only / everyone), limits per plan, the models,
// the monthly budget, this month's use, the support knowledge, and the markdown text
// of each market report (what the market assistant may quote to an owner).
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Bot, Upload, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { pathId } from '../../lib/pathId';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PLANS = ['free', 'standard', 'premium', 'enterprise'];
const SWITCH_LABEL = { off: 'Off', admins: 'Admins only (testing)', everyone: 'Everyone' };

async function readError(res) {
  const data = await res.json().catch(() => ({}));
  return typeof data.detail === 'string' ? data.detail : `The server answered ${res.status}.`;
}

const readFile = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result || ''));
  r.onerror = () => reject(new Error('Could not read the file.'));
  r.readAsText(file, 'utf-8');
});

export default function AdminChatAgentsCard() {
  const { getToken } = useAuth();
  const [s, setS] = useState(null);
  const [form, setForm] = useState(null);
  const [usage, setUsage] = useState(null);
  const [know, setKnow] = useState({});
  const [reports, setReports] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const headers = useCallback(() => ({ Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' }), [getToken]);

  const call = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API_URL}${path}`, { headers: headers(), ...opts });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }, [headers]);

  const load = useCallback(async () => {
    try {
      const [st, us, kc, km, rt] = await Promise.all([
        call('/api/chat/admin/settings'),
        call('/api/chat/admin/usage'),
        call('/api/chat/admin/knowledge/support-core'),
        call('/api/chat/admin/knowledge/support-manual'),
        call('/api/chat/admin/report-texts'),
      ]);
      setS(st);
      setForm({
        support_model: st.support_model, market_model: st.market_model, planner_model: st.planner_model,
        support_per_day: st.support_per_day, monthly_budget_usd: st.monthly_budget_usd,
        market_per_month: { ...st.market_per_month }, market_records: { ...st.market_records },
      });
      setUsage(us);
      setKnow({ 'support-core': kc, 'support-manual': km });
      setReports(rt);
      setError(null);
    } catch (e) {
      setError(e.message || 'Could not reach the server.');
    }
  }, [call]);

  useEffect(() => { load(); }, [load]);

  const put = async (patch, ok) => {
    setBusy(true);
    try {
      const st = await call('/api/chat/admin/settings', { method: 'PUT', body: JSON.stringify(patch) });
      setS(st);
      toast.success(ok);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveForm = () => {
    const num = (v) => (v === '' || v == null ? NaN : Number(v));
    const ints = (o) => Object.fromEntries(PLANS.map((p) => [p, parseInt(o[p], 10)]));
    put({
      support_model: form.support_model.trim(), market_model: form.market_model.trim(), planner_model: form.planner_model.trim(),
      support_per_day: parseInt(form.support_per_day, 10), monthly_budget_usd: num(form.monthly_budget_usd),
      market_per_month: ints(form.market_per_month), market_records: ints(form.market_records),
    }, 'Limits and models saved.');
  };

  const uploadKnowledge = async (kid, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const text = await readFile(file);
      const r = await call(`/api/chat/admin/knowledge/${kid}`, { method: 'PUT', body: JSON.stringify({ text }) });
      toast.success(`Saved: ${r.chars.toLocaleString()} characters, ${r.sections} sections.`);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const uploadReport = async (id, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const text = await readFile(file);
      const r = await call(`/api/chat/admin/report-texts/${pathId(id)}`, {
        method: 'PUT', body: JSON.stringify({ text, filename: file.name }),
      });
      toast.success(`${r.title}: ${r.sections} sections indexed.`);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const removeReport = async (id) => {
    setBusy(true);
    try {
      await call(`/api/chat/admin/report-texts/${pathId(id)}`, { method: 'DELETE' });
      toast.success('Report text removed. The assistant now shows only its catalogue summary.');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const SwitchRow = ({ k, label }) => (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <Label className="font-semibold">{label}</Label>
      <div className="flex gap-1">
        {(s.switch_values || ['off', 'admins', 'everyone']).map((v) => (
          <Button key={v} size="sm" variant={s[k] === v ? 'default' : 'outline'} disabled={busy}
            onClick={() => put({ [k]: v }, `${label}: ${SWITCH_LABEL[v]}.`)} data-testid={`chat-switch-${k}-${v}`}>
            {SWITCH_LABEL[v]}
          </Button>
        ))}
      </div>
    </div>
  );

  return (
    <Card className="border-0 bg-card/50" data-testid="admin-chat-agents-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Bot className="w-5 h-5 text-primary" />AI chat assistants</CardTitle>
        <CardDescription>
          MATT (support) answers only from the support knowledge below. ANTHONY (market) answers only from the
          News, Tenders, Projects and Companies databases as each customer sees them, plus up to 2 market reports
          (full text only for reports the customer owns). No web search. Conversations are deleted after 90 days.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && <p className="text-sm text-destructive">{error}</p>}
        {s && form && (
          <>
            <div className="space-y-3">
              <SwitchRow k="support_on_for" label="Support assistant (MATT)" />
              <SwitchRow k="market_on_for" label="Market assistant (ANTHONY)" />
            </div>

            {usage && (
              <div className="rounded-lg bg-muted/30 p-3 text-sm space-y-1" data-testid="chat-usage">
                <p className="font-semibold">This month ({usage.month}): US${usage.spend_usd.toFixed(2)} of US${Number(s.monthly_budget_usd).toFixed(2)} budget</p>
                {['support', 'market'].map((a) => (
                  <p key={a}>{a === 'support' ? 'Support' : 'Market'}: {usage.agents[a].messages} messages from {usage.agents[a].people} people
                    {a === 'support' ? `, ${usage.agents[a].handed_over} passed to support@` : ''}</p>
                ))}
                {usage.unanswered_support_questions && usage.unanswered_support_questions.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer">Questions passed to support@ (add the answers to the knowledge)</summary>
                    <ul className="mt-1 list-disc ps-5 text-xs space-y-1">
                      {usage.unanswered_support_questions.map((q, i) => (
                        <li key={i}><Badge variant="outline" className="me-1">{q.reason}</Badge>{q.question}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}

            <div className="space-y-3">
              <p className="font-semibold text-sm">Limits, models and budget</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {['support_model', 'market_model', 'planner_model'].map((k) => (
                  <div key={k} className="space-y-1">
                    <Label className="text-xs">{{ support_model: 'Support model', market_model: 'Market model', planner_model: 'Search-planning model' }[k]}</Label>
                    <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                  </div>
                ))}
                <div className="space-y-1">
                  <Label className="text-xs">Support messages per person per day</Label>
                  <Input type="number" value={form.support_per_day} onChange={(e) => setForm({ ...form, support_per_day: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Monthly budget, US$ (both assistants stop for customers when reached)</Label>
                  <Input type="number" value={form.monthly_budget_usd} onChange={(e) => setForm({ ...form, monthly_budget_usd: e.target.value })} />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="text-sm w-full">
                  <thead><tr className="text-xs text-muted-foreground"><th className="text-start p-1">Plan</th><th className="text-start p-1">Market questions / month</th><th className="text-start p-1">Records listed per answer</th></tr></thead>
                  <tbody>
                    {PLANS.map((p) => (
                      <tr key={p}>
                        <td className="p-1 capitalize">{p}</td>
                        <td className="p-1"><Input type="number" className="h-8 w-24" value={form.market_per_month[p]}
                          onChange={(e) => setForm({ ...form, market_per_month: { ...form.market_per_month, [p]: e.target.value } })} /></td>
                        <td className="p-1"><Input type="number" className="h-8 w-24" value={form.market_records[p]}
                          onChange={(e) => setForm({ ...form, market_records: { ...form.market_records, [p]: e.target.value } })} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button variant="outline" disabled={busy} onClick={saveForm} data-testid="chat-save-limits">Save limits and models</Button>
            </div>

            <div className="space-y-3">
              <p className="font-semibold text-sm">Support knowledge (markdown)</p>
              {['support-core', 'support-manual'].map((kid) => {
                const k = know[kid] || {};
                return (
                  <div key={kid} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-muted/30 p-3">
                    <div className="text-sm">
                      <p className="font-medium">{k.label || kid}</p>
                      <p className="text-xs text-muted-foreground">
                        {k.chars ? `${k.chars.toLocaleString()} characters, ${k.sections} sections, updated ${k.updated_at ? new Date(k.updated_at).toLocaleString() : '—'}` : 'Empty — the support assistant cannot answer until this is loaded.'}
                      </p>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer text-sm border rounded-md px-3 py-1.5 hover:bg-muted">
                      <Upload className="w-4 h-4" /> Replace with a .md file
                      <input type="file" accept=".md,.txt,text/markdown,text/plain" className="hidden" disabled={busy}
                        onChange={(e) => { uploadKnowledge(kid, e.target.files[0]); e.target.value = ''; }} data-testid={`chat-knowledge-${kid}`} />
                    </label>
                  </div>
                );
              })}
            </div>

            {reports && (
              <div className="space-y-2">
                <p className="font-semibold text-sm">Market report texts — {reports.with_text} of {reports.total} reports have their markdown</p>
                <div className="max-h-80 overflow-y-auto divide-y rounded-lg border">
                  {reports.reports.map((r) => (
                    <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 p-2 text-sm">
                      <div>
                        <p className="font-medium">{r.title} {r.country ? <span className="text-xs text-muted-foreground">· {r.country}</span> : null}</p>
                        <p className="text-xs text-muted-foreground">{r.has_text ? `${r.sections} sections · ${r.filename || ''}` : 'No text: the assistant shows only the catalogue summary'}</p>
                      </div>
                      <div className="flex gap-2">
                        <label className="inline-flex items-center gap-1 cursor-pointer text-xs border rounded-md px-2 py-1 hover:bg-muted">
                          <Upload className="w-3 h-3" /> {r.has_text ? 'Replace' : 'Upload .md'}
                          <input type="file" accept=".md,.txt,text/markdown,text/plain" className="hidden" disabled={busy}
                            onChange={(e) => { uploadReport(r.id, e.target.files[0]); e.target.value = ''; }} />
                        </label>
                        {r.has_text && (
                          <Button size="sm" variant="ghost" disabled={busy} onClick={() => removeReport(r.id)} aria-label="Remove text">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
